import React from 'react';
import { Brain, Heart, ArrowRight } from 'lucide-react';

const SKILLS = [
  { key: 'awareness', label: 'Awareness', Icon: Brain },
  { key: 'acceptance', label: 'Acceptance', Icon: Heart },
  { key: 'action', label: 'Action', Icon: ArrowRight },
];

/**
 * QMGSymbols — the Awareness · Acceptance · Action triad.
 *
 * Props:
 *   active      'awareness' | 'acceptance' | 'action' | null  (highlights one skill)
 *   size        'sm' | 'md' | 'lg'                            (default 'md')
 *   tone        'light' | 'dark'                              (default 'dark' — for white backgrounds)
 *   className   extra classes for the wrapper
 */
const QMGSymbols = ({ active = null, size = 'md', tone = 'dark', className = '' }) => {
  const sizeMap = {
    sm: { box: 'w-7 h-7', icon: 'w-4 h-4', text: 'text-[10px]', gap: 'gap-2', sep: 'w-6' },
    md: { box: 'w-10 h-10', icon: 'w-5 h-5', text: 'text-xs', gap: 'gap-3', sep: 'w-10' },
    lg: { box: 'w-14 h-14', icon: 'w-7 h-7', text: 'text-sm', gap: 'gap-4', sep: 'w-12' },
  };
  const s = sizeMap[size] || sizeMap.md;

  const isLight = tone === 'light';
  const baseTextColor = isLight ? 'text-white/80' : 'text-[#57534E]';
  const activeTextColor = isLight ? 'text-white' : 'text-[#1A1A1A]';
  const baseBoxColor = isLight ? 'border-white/30 text-white/70' : 'border-[#1A2E1D]/20 text-[#1A2E1D]/70';
  const activeBoxColor = isLight
    ? 'border-white bg-white/10 text-white shadow-sm'
    : 'border-[#1A2E1D] bg-[#1A2E1D] text-white shadow-sm';
  const sepColor = isLight ? 'bg-white/30' : 'bg-[#1A2E1D]/20';

  return (
    <div
      className={`inline-flex items-center ${s.gap} ${className}`}
      data-testid="qmg-symbols"
      role="group"
      aria-label="Quiet Mind Golf Blueprint: Awareness, Acceptance, Action"
    >
      {SKILLS.map(({ key, label, Icon }, i) => {
        const isActive = active === key;
        return (
          <React.Fragment key={key}>
            <div className="flex items-center gap-2" data-testid={`qmg-symbol-${key}`}>
              <span
                className={`flex items-center justify-center rounded-full border transition-colors ${s.box} ${
                  isActive ? activeBoxColor : baseBoxColor
                }`}
              >
                <Icon className={s.icon} strokeWidth={2} />
              </span>
              <span
                className={`uppercase tracking-[0.2em] font-semibold ${s.text} ${
                  isActive ? activeTextColor : baseTextColor
                }`}
              >
                {label}
              </span>
            </div>
            {i < SKILLS.length - 1 && (
              <span className={`hidden sm:block h-px ${s.sep} ${sepColor}`} aria-hidden="true" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default QMGSymbols;
